<script lang="ts" setup>
import {
  ClubSortField,
  ListClubsFilter,
  SortOrder
} from '__generated__/globalTypes'
import { listAmenities } from '__generated__/listAmenities'
import { listClubs } from '__generated__/listClubs'

import { ref, toRefs } from 'vue'
import { useRouter, useRoute, onBeforeRouteUpdate } from 'vue-router'
import { useHead } from '@vueuse/head'

import { Card, CardType } from '@base/_types/Card'
import { useToastsStore } from '@base/store/toastsStore'
import { ToastSemantic } from '@base/_types/Toast'

import { LIST_CLUBS_QUERY } from '@base/api/queries/listClubs'
import { useClient } from '@base/api/useClient'
import { ugcImageSrc } from '@base/lib/ugcImageSrc'
import { createSlug } from '@base/lib/createSlug'
import { createDistanceContent } from '@base/lib/createDistanceContent'

import CardList from '@base/components/CardList/CardList.vue'
import ImageWrapper from '@base/components/ImageWrapper/ImageWrapper.vue'
import USHeroBanner from '@base/components/USHeroBanner/USHeroBanner.vue'
import USIcon from '@base/components/USIcon/USIcon.vue'
import USInputSort from '@base/components/USInputSort/USInputSort.vue'
import USPagination from '@base/components/USPagination/USPagination.vue'

import heroBannerImage from '@player/_assets/indoor-golf-cover.jpg'

import ClubSearchBar from '@player/_components/ClubSearchBar/ClubSearchBar.vue'

import { playerTitle } from '@player/_lib/playerTitle'

import { clubsRouteNames } from '@player/clubs/router/routeNames'

import { LIST_AMENITIES } from '@player/courses/api/queries/listAmenities'

import { useClubsStore } from '@player/clubs/store/clubsStore'

import imageClubLogoPlaceholder from '@player/_assets/club-logo-placeholder.jpg'
import imageEventPlaceholder from '@player/_assets/event-placeholder.jpg'

import { props as propDefinition } from './props'

type ClubSearchBarSelectType = {
  label: string
  value: string
}

type ClubSearchEvent = {
  lat: number | null
  lon: number | null
  loc: string | null
  amenitiesValue: string[] | []
}

// Props
const props = defineProps(propDefinition)

// Static Variables
const limit = 10
const client = useClient()
const router = useRouter()
const route = useRoute()
const clubsStore = useClubsStore()
const apiKey = import.meta.env.VITE_GCP_GEOCODING_API_KEY
const toastsStore = useToastsStore()

useHead({
  title: playerTitle('Indoor Golf')
})

const sortOptions: any[] = [
  {
    label: 'A-Z',
    value: ClubSortField.NAME
  },
  {
    label: 'Near me',
    value: ClubSortField.DISTANCE
  }
]

// Inputs
const currentSort = ref<string>(ClubSortField.NAME)
const currentPage = ref<number>(1)
const lat = ref<number | null>()
const lon = ref<number | null>()
const loc = ref<string | null>()
const amenities = ref<string[]>([])
const loading = ref<boolean>(false)
const cards = <Card[]>[]

const { sort } = toRefs(props)

currentSort.value = sort?.value || ClubSortField.NAME
currentPage.value = props.page || 1
lat.value = props.lat ? props.lat : undefined
lon.value = props.lon ? props.lon : undefined
loc.value = props.loc || undefined
amenities.value = props.amenities ? props.amenities.split(',') : []

// Outputs
const { clubs, count, serverError } = toRefs(clubsStore)
const maxPage = ref<number>(1)
const clubAmenities = ref<ClubSearchBarSelectType[]>([])
const results = ref<string>('')

const clubSearchBarProps = {
  apiKey: apiKey,
  buttonLabel: 'Search',
  amenitiesTypeSelect: clubAmenities.value,
  showAdvanced: false,
  amenities: amenities.value,
  lat: lat.value,
  lon: lon.value,
  loc: loc.value
}

// Helper Functions
const reset = () => {
  serverError.value = false
  clubs.value = []
  count.value = 0
  results.value = ''
  loading.value = false
}

const handleCount = () => {
  results.value = ''
  let minResults = 0
  let maxResults = 0
  if (count.value > 0) {
    if (currentPage.value - 1 > 0) {
      minResults = (currentPage.value - 1) * limit
    } else {
      minResults = currentPage.value - 1
    }

    if (count.value < currentPage.value * limit) {
      maxResults = count.value
    } else {
      maxResults = Math.ceil(currentPage.value * limit)
    }
    results.value += `${minResults} - ${maxResults}`
  }

  results.value += ` of ${count.value}`
}

const handleSort = (e: [string]) => {
  currentPage.value = 1
  if (e[0] === ClubSortField.NAME) {
    currentSort.value = ClubSortField.NAME
    router.replace({
      query: { ...route.query, sort: undefined },
      params: { page: undefined }
    })
  } else if (e[0] === ClubSortField.DISTANCE) {
    currentSort.value = ClubSortField.DISTANCE
    router.replace({
      query: { ...route.query, sort: ClubSortField.DISTANCE },
      params: { page: undefined }
    })
  }
}

const handlePagination = (page: number) => {
  currentPage.value = page
  router.push({ params: { page: page }, query: router.currentRoute.value.query })
}

const generateClubLogo = (logo: string, clubName: string) => {
  return {
    src: logo ? ugcImageSrc(logo || '')[0] : imageClubLogoPlaceholder,
    srcset: logo ? ugcImageSrc(logo || '')[0] : imageClubLogoPlaceholder,
    alt: `The logo for ${clubName}`
  }
}

const generateClubFeaturedImage = (
  clubName: string,
  featuredImage: string[]
) => {
  return {
    src:
      featuredImage && featuredImage[0]
        ? ugcImageSrc(featuredImage[0] || '')[0]
        : imageEventPlaceholder,
    srcset:
      featuredImage && featuredImage[0]
        ? ugcImageSrc(featuredImage[0] || '')[0]
        : imageEventPlaceholder,
    alt: `The featuredImage for ${clubName}`
  }
}

const handleClubSearch = (e: ClubSearchEvent) => {
  const query: any = {
    sort: strToSort(currentSort.value)[0]
  }

  // If a location was selected via the ClubSearchBar, let's set our lat/lon/loc
  // values and add the data to the querystring
  if (e.lat && e.lon && e.loc) {
    lat.value = e.lat
    lon.value = e.lon
    loc.value = e.loc

    query.lat = e.lat
    query.lon = e.lon
    query.loc = e.loc

    // If a location was selected, let's set the sort to DISTANCE
    query.sort = ClubSortField.DISTANCE
    currentSort.value = ClubSortField.DISTANCE
  }

  amenities.value = e.amenitiesValue
  query.amenities = e.amenitiesValue
  
  currentPage.value = 1
  router.push({ query, params: { page: undefined} })
}

const formatClubCards = () => {
  while(cards.length > 0) { cards.pop() }
  clubs.value.forEach((club) => {
    if (
      club.__typename === 'Club' &&
      club !== null
    ) {
      let clubLogo = generateClubLogo(
        club.logo || '',
        club.clubName
      )

      let featuredImage = generateClubFeaturedImage(
        club.clubName,
        club.gallery || []
      )

      cards.push({
        type: CardType.CardListingCourse,
        data: {
          club: {
            name: club.clubName,
            rating: club.averageRating || 0,
            logo: clubLogo,
            to: {
              name: clubsRouteNames.ClubSingle,
              params: {
                slug: createSlug(
                  club.clubName,
                  club.slug
                )
              }
            }
          },
          course: {
            name: club.clubName,
            holes: 0,
            par: 0,
            price: '',
            featuredImage: featuredImage,
            status: '',
            location: club.city || '',
            distance:
              club.addressGeom && lat.value && lon.value
                ? createDistanceContent(club.addressGeom, {
                    lat: lat.value,
                    lon: lon.value
                  })
                : '',
            to: {
              teeTimes: {
                name: clubsRouteNames.ClubSingle,
                params: {
                  slug: createSlug(club.clubName, club.slug)
                }
              }
            }
          }
        }
      })
    }
  })
}

const strToSort = (str: string | undefined): [ClubSortField, SortOrder] => {
  if (str === ClubSortField.NAME) {
    return [ClubSortField.NAME, SortOrder.ASC]
  } else if (str === ClubSortField.DISTANCE) {
    return [ClubSortField.DISTANCE, SortOrder.ASC]
  }
  return [ClubSortField.NAME, SortOrder.ASC]
}

// Query Functions

const fetchListOfAmenities = async () => {
  try {
    const { data } = await client.query<listAmenities>({
      query: LIST_AMENITIES,
      fetchPolicy: 'cache-first'
    })

    if (!data || data.fetchAmenities.__typename !== 'AttributesOutput') {
      return
    }

    data.fetchAmenities.attributes.forEach((item) => {
      clubAmenities.value.push({
        label: item.name,
        value: item.id
      })
    })

    let { data: listClubsData } = await client.query<listClubs>({
      query: LIST_CLUBS_QUERY,
      variables: {
        filter: {},
        sortField: ClubSortField.NAME,
        sortOrder: SortOrder.ASC,
        offset: 0,
        limit: 0
      },
      fetchPolicy: 'cache-first'
    })

  } catch (e) {
    console.error(e)
    serverError.value = true
  }
}

const fetchData = async () => {
  reset()
  loading.value = true
  try {

    const [sortField, sortOrder] = strToSort(currentSort.value)

    // If we already have lat/lon values (from the navigator or from a location search)
    // simply proceed with our query.
    if (lat.value && lon.value) {
      // Do nothing
    }

    // If the location data has been provided via the querystring, let's set our
    // lat/lon variables and proceed
    else if (route.query.lat && route.query.lon) {
      lat.value = props.lat
      lon.value = props.lon
    }

    // If we do not have lat/lon values yet, grab them from the navigator
    else {
      const navigatorPromise = new Promise((resolve, reject) => {
        if (navigator && navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              lat.value = position.coords.latitude
              lon.value = position.coords.longitude
              resolve(true);
            },
            () => {
              console.error('user declined')
              reject()
            }
          )
        } else {
          toastsStore.pushToast({
            content: 'Your browser dose not support geolocation',
            icon: 'exclamation-square',
            semantic: ToastSemantic.Error
          })
          reject()
        }
      })

      // If the current sort is by distance, let's wait for the navigator promise
      // to resolve so that the sort works properly. If not, we can let that happen
      // asynchronously behind the scenes. This should only happen when the user 
      // navigates directly to a copied URL with the sort in the query string.
      if (sortField === ClubSortField.DISTANCE) {
        await navigatorPromise;
      }
    }

    const filter: ListClubsFilter = {
      isNonGolf: true,
      addressGeom:
        lat.value !== undefined &&
        lat.value !== null &&
        lon.value !== undefined &&
        lon.value !== null
          ? { lat: lat.value, lon: lon.value }
          : undefined,
      amenities: amenities.value
    }

    const offset = (currentPage.value - 1) * limit
    await clubsStore.listClubs(
      client,
      filter,
      sortField,
      sortOrder,
      offset,
      limit
    )

    if (serverError.value === true) return
    maxPage.value = Math.ceil(count.value / limit)
    handleCount()
    formatClubCards()
  } catch (e) {
    console.error(e)
    serverError.value = true
  } finally {
    loading.value = false
  }
}

// Fetch Data when the URL changes the props

onBeforeRouteUpdate(async () => {
  await fetchData()
})

// Fetch data when the view is first rendered
await fetchListOfAmenities()
fetchData()
</script>

<template>
  <div class="clubs">
    <USHeroBanner class="clubs__hero-banner">
      <div class="overlay"></div>
      <ImageWrapper :src="heroBannerImage" object-fit="cover" height="100%" />
      <div class="clubs__hero-banner-content">
        <div class="grid__row">
          <div class="grid__column grid__column--12">
            <div class="font-color--neutral-a5 text-align--center">
              <p class="display--d1">Find Your Fun — Indoors</p>
            </div>
          </div>
        </div>
      </div>
    </USHeroBanner>
    <div class="grid__row clubs__row-containing-searchbar">
      <div class="grid__column grid__column--12 grid__column--breakpoint--13--10 grid__column--breakpoint--13--offset--1">
        <div class="clubs__searchbar-wrapper">
          <ClubSearchBar
            v-bind="clubSearchBarProps"
            @submitClubSearch="handleClubSearch($event)"
          />
        </div>
      </div>
    </div>
    <template v-if="loading">
      <div class="grid__row">
        <div class="grid__column grid__column--12">
          <USIcon name="spinner" css-diameter="22px" class="clubs__loading" />
        </div>
      </div>
    </template>
    <template v-else-if="clubs && clubs.length">
      <div class="grid__row">
        <div class="grid__column grid__column--12">
          <div class="clubs__listing-controls margin-top--6 margin-bottom--8">
            <p class="body--p2--regular font-color--neutral-a3">
              {{ results }}
            </p>
            <USInputSort
              :model-value="[currentSort]"
              select-text="Sort"
              :options="sortOptions"
              @update:model-value="handleSort"
            />
          </div>
        </div>
      </div>
      <CardList :cards="cards" />
      <div>
        <USPagination
          v-if="maxPage > 1"
          :min="1"
          :max="maxPage"
          :current="currentPage"
          class="margin-top--5"
          @page-goto="handlePagination"
          @page-next="handlePagination"
          @page-previous="handlePagination"
        />
      </div>
    </template>
    <template v-else>
      <div class="grid__row">
        <div class="grid__column grid__column--12">
          <p class="body-p1--regular text-align--center margin-top--6">
            Hmmm! we don't seem to have any clubs that meet your search
            criteria. Try another search.
          </p>
        </div>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@use '@base/styles/v1.0/scss/foundations/breakpoint';

$search-bar-offset-sm: 30px;
$search-bar-offset-lg: 50px;

.clubs {
  &__hero-banner {
    margin-top: calc(-1 * var(--spacing--5));

    .overlay {
      z-index: 1;
      position: absolute;
      width: 100%;
      height: 100%;
      background-color: black;
      opacity: .15;
    }
  }

  &__hero-banner-content {
    z-index: 2;
    position: absolute;
    left: 0;
    top: calc(50%);
    transform: translateY(-50%);
    width: 100%;
  }

  &__row-containing-searchbar {
    margin-top: #{$search-bar-offset-sm * -1};

    @include breakpoint.min-width(breakpoint.$breakpoint--9) {
      margin-top: #{$search-bar-offset-lg * -1};
    }
  }

  &__listing-controls {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  &__loading {
    animation: rotation 1s infinite ease-in-out;
    margin: 0 auto;
  }

  @keyframes rotation {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(359deg);
    }
  }
}
</style>
